import React, { useImperativeHandle } from 'react'

import {
  Card,
  Checkbox,
  DangerButton,
  Divider,
  IconThumbsDown,
  IconThumbsUp,
  PositiveButton,
  Space,
  Text,
} from '@procurify/ui'

import { PaginationControls } from '@webapp/core/components/PaginationControls'
import { useProcurifyIntl } from '@webapp/hooks'
import { type TravelListStatus } from '@webapp/modules/expense/business-logic/types/TravelListStatus'
import { type TravelStatusCounts } from '@webapp/modules/expense/business-logic/types/TravelStatusCounts'
import messages from './messages'
import {
  useApprovableTravelListPresenter,
  type TravelApprovalSelection,
} from './useApprovableTravelListPresenter'
import { ApprovableTravelCards } from '../../views/ApprovableTravelCards'

export interface ApprovableTravelListPresenterRef {
  clearSelection: () => void
}

export interface IApprovableTravelListPresenterProps {
  status: TravelListStatus
  subscribedApproverId?: number
  /** Pending tab && no watchlist filter — computed by the Feature Set (BHV-032). */
  isSelectable: boolean
  justUpdatedId?: number
  /** True while a batch command is submitting (BHV-033 double-submit guard). */
  disableBulkActions: boolean
  onSelectionChange: (payload: TravelApprovalSelection[]) => void
  onCountsChange: (counts: TravelStatusCounts) => void
  onApprove: () => void
  onDeny: () => void
}

export const ApprovableTravelListPresenter = React.forwardRef<
  ApprovableTravelListPresenterRef,
  IApprovableTravelListPresenterProps
>(
  (
    {
      status,
      subscribedApproverId,
      isSelectable,
      justUpdatedId,
      disableBulkActions,
      onSelectionChange,
      onCountsChange,
      onApprove,
      onDeny,
    },
    ref
  ) => {
    const intl = useProcurifyIntl()

    const {
      data: { travels },
      states: {
        isLoading,
        selectedIds,
        allVisibleSelected,
        approverIdByTravelId,
      },
      methods,
    } = useApprovableTravelListPresenter({
      status,
      subscribedApproverId,
      isSelectable,
      onSelectionChange,
      onCountsChange,
    })

    useImperativeHandle(
      ref,
      () => ({ clearSelection: methods.clearSelection }),
      [methods.clearSelection]
    )

    const sortKeys = [
      {
        label: intl.formatMessage(messages.sortByTravelNumberLabel),
        value: 'id',
      },
    ]

    const bulkActionsDisabled = selectedIds.size === 0 || disableBulkActions

    return (
      <Card>
        <Card.Content>
          <PaginationControls id='travel-approval-list'>
            <Space direction='horizontal' justify='space-between'>
              <Space direction='horizontal'>
                <PaginationControls.Search
                  placeholder={intl.formatMessage(messages.searchPlaceholder)}
                />
                <Space.Item minWidth={200}>
                  <PaginationControls.Sort sortKeys={sortKeys} />
                </Space.Item>
              </Space>
              <PaginationControls.Page />
            </Space>
          </PaginationControls>
        </Card.Content>

        <Divider space={0} />

        {isSelectable && (
          <Card.Meta>
            <Space direction='horizontal' align='center'>
              <Checkbox
                checked={allVisibleSelected}
                onChange={(e) => methods.onToggleSelectAll(e.target.checked)}
                inputProps={{
                  'aria-label': intl.formatMessage(messages.selectAllLabel),
                }}
              />
              <DangerButton
                iconComponent={<IconThumbsDown />}
                onClick={onDeny}
                disabled={bulkActionsDisabled}
              >
                {intl.formatMessage(messages.denyTravelButton)}
              </DangerButton>
              <PositiveButton
                iconComponent={<IconThumbsUp />}
                onClick={onApprove}
                disabled={bulkActionsDisabled}
              >
                {intl.formatMessage(messages.approveTravelButton)}
              </PositiveButton>

              {selectedIds.size > 0 && (
                <Text>
                  {intl.formatMessage(messages.selectedTravelsCount, {
                    count: selectedIds.size,
                  })}
                </Text>
              )}
            </Space>
          </Card.Meta>
        )}

        <ApprovableTravelCards
          travels={travels}
          isLoading={isLoading}
          isSelectable={isSelectable}
          selectedIds={selectedIds}
          approverIdByTravelId={approverIdByTravelId}
          justUpdatedId={justUpdatedId}
          onToggleSelect={methods.onToggleSelect}
          onApproverPick={methods.onApproverPick}
        />

        <Card.Content>
          <PaginationControls id='travel-approval-list'>
            <Space direction='horizontal' justify='space-between'>
              <PaginationControls.PageLimit />
              <PaginationControls.Page />
            </Space>
          </PaginationControls>
        </Card.Content>
      </Card>
    )
  }
)

ApprovableTravelListPresenter.displayName = 'ApprovableTravelListPresenter'
